<template>
	<div class="selector-wrap">
		<el-popover
			popper-class="year-dropdown"
			:visible-arrow="false"
			width="120"
			placement="bottom"
			trigger="click"
			v-model="visibleDropdown"
		>
			<el-scrollbar style="height: 100%">
				<div class="year-dropdown-content">
					<div
						v-for="year in years"
						:key="year"
						class="year-dropdown-item"
						:class="{ active: year === currentYear }"
						@click="changeYear(year)"
					>
						<span>{{ year }}</span>
						<SvgIcons v-show="year === currentYear" name="gouxuan" class="year-dropdown-icon" />
					</div>
				</div>
			</el-scrollbar>
			<div class="year-selector" slot="reference">
				<span class="year-text">{{ currentYear }}</span>
				<SvgIcons name="down" class="year-arrow" :class="{ 'rotate': visibleDropdown }" />
			</div>
		</el-popover>
		<div class="right-wrap">
			<div class="separator"></div>
			<div class="month-selector">
				<div
					class="month-item"
					v-for="month in months"
					:key="month.value"
					:class="{ active: month.value === currentMonth }"
					@click="changeMonth(month.value)"
				>
					{{ month.label }}
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref, computed, defineEmits, onMounted, watch } from 'vue';

const emit = defineEmits(['change']);

const props = defineProps({
	defaultValue: {
		type: [String, Date],
		default: ''
	}
});

// 解析日期的函数（支持 Date 对象或时间戳字符串）
const parseDateTime = dateInput => {
	if (!dateInput) {
		// 如果没有传入日期，使用当前时间
		const nowDate = new Date();
		return {
			year: nowDate.getFullYear(),
			month: nowDate.getMonth() + 1
		};
	}

	let date;
	if (dateInput instanceof Date) {
		// 如果传入的是 Date 对象，直接使用
		date = dateInput;
	} else {
		// 如果传入的是字符串，解析为 Date 对象
		date = new Date(dateInput);
	}

	if (isNaN(date.getTime())) {
		// 如果日期无效，回退到当前时间
		const nowDate = new Date();
		return {
			year: nowDate.getFullYear(),
			month: nowDate.getMonth() + 1
		};
	}

	return {
		year: date.getFullYear(),
		month: date.getMonth() + 1
	};
};

// 从默认值或当前时间解析选中的年月
const { year: initialYear, month: initialMonth } = parseDateTime(props.defaultValue);

const currentYear = ref(initialYear);
const currentMonth = ref(initialMonth);
const visibleDropdown = ref(false);

// 组件挂载时触发初始变化事件
onMounted(() => {
	emit('change', {
		year: currentYear.value,
		month: currentMonth.value,
		state: 'init'
	});
});

// 年份列表：始终以当前年份为基准，往前20年，往后1年
const years = computed(() => {
	const yearList = [];
	const todayYear = new Date().getFullYear();
	const startYear = todayYear - 20;
	const endYear = todayYear + 1;

	for (let year = startYear; year <= endYear; year++) {
		yearList.push(year);
	}

	return yearList;
});

// 月份列表：固定12个月
const months = computed(() => {
	const monthList = [];
	for (let month = 1; month <= 12; month++) {
		monthList.push({
			value: month,
			label: `${month}月`
		});
	}
	return monthList;
});

const changeYear = year => {
	currentYear.value = year;
	visibleDropdown.value = false;
	// 抛出变化事件
	emit('change', {
		year: currentYear.value,
		month: currentMonth.value
	});
};

const changeMonth = month => {
	currentMonth.value = month;
	// 抛出变化事件
	emit('change', {
		year: currentYear.value,
		month: currentMonth.value
	});
};

watch(
	() => props.defaultValue,
	newValue => {
		if (newValue) {
			const { year, month } = parseDateTime(newValue);
			currentYear.value = year;
			currentMonth.value = month;

			// 触发变化事件
			emit('change', {
				year: currentYear.value,
				month: currentMonth.value,
				state: 'update'
			});
		}
	}
);
</script>

<style lang="less">
.year-dropdown {
	&.el-popper {
		margin-top: 0;
		padding: 0;
		box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
		border: 1px solid #e4e7ed;
		border-radius: 2px;
		height: 110px;
	}

	.year-dropdown-content {
		padding: 4px;

		.year-dropdown-item {
			padding: 0 12px;
			height: 32px;
			display: flex;
			align-items: center;
			justify-content: space-between;
			color: rgba(0, 0, 0, 0.6);
			cursor: pointer;

			span {
				font-size: 14px;
			}
			.year-dropdown-icon {
				font-size: 16px;
				color: #000 !important;
			}

			&.active {
				color: #000;

				span {
					font-size: 16px;
				}
			}
		}
	}
	.el-scrollbar__wrap {
		overflow-x: hidden;
	}
}
</style>

<style lang="less" scoped>
.selector-wrap {
	display: flex;
	align-items: flex-start;

	.year-selector {
		display: flex;
		align-items: center;
		cursor: pointer;

		.year-text {
			color: #000;
			font-family: D-DIN;
			font-size: 40px;
			font-style: normal;
			font-weight: 700;
			line-height: 48px; /* 120% */
		}

		.year-arrow {
			margin-left: 8px;
			font-size: 16px;
			color: #000000 !important;
			transition: transform 0.3s ease;

			&.rotate {
				transform: rotate(180deg);
			}
		}
	}
	.right-wrap {
		padding: 10px 0;
		position: relative;
		padding-left: 64px;

		.separator {
			position: absolute;
			top: 8px;
			left: 0;
			bottom: 8px;
			width: 64px;

			&::before {
				content: '';
				position: absolute;
				top: 0;
				left: 0;
				right: 0;
				bottom: 0;
				margin: auto;
				width: 1px;
				background: rgba(0, 0, 0, 0.4);
				transform: scaleX(0.5);
				pointer-events: none;
			}
		}
	}
	.month-selector {
		display: flex;
		flex-wrap: wrap;
		gap: 16px;

		.month-item {
			padding: 5px 12px;
			border-radius: 4px;
			background: rgba(0, 0, 0, 0.04);
			cursor: pointer;
			color: rgba(0, 0, 0, 0.6);
			font-size: 14px;
			font-style: normal;
			font-weight: 400;
			line-height: 18px; /* 128.571% */
			transition: all 0.3s ease;

			&.active {
				border-radius: 4px;
				background: var(--theme-color);
				color: #fff;
			}
		}
	}
}
</style>
